import React, { Fragment } from "react";
import {
  Button,
  Container,
  Typography,
  CssBaseline,
  Box,
  Stack,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { PREMIUM_HOME_CTA, PREMIUM_PURCHASING_BUTTON } from "./billing";
import { SignInStatus } from "./states";

interface Feature {
  src: string;
  // キャプションの文言。読みやすい位置で改行したいので行ごとに分けて持つ。
  // alt はこれらを連結したものを使う。
  labelLines: string[];
}

const FEATURES: Feature[] = [
  { src: "./imgs/home_img1.png", labelLines: ["翻译不影响版面布局"] },
  {
    src: "./imgs/home_img2.png",
    labelLines: ["可以在2个页面左右对比", "翻译前后的PDF文件"],
  },
  { src: "./imgs/home_img3.png", labelLines: ["翻译文件大约只需要 30 秒"] },
];

interface Cta {
  label: string;
  to: string;
}

const FREE_TRANSLATE_CTA: Cta = {
  label: "点击此处在线翻译（免费版）",
  to: "/translate",
};

// 認証状態ごとに出すボタンの唯一の定義。
// SignInStatus に値を足したらここでコンパイルエラーになる。
const ctasFor = (signInStatus: SignInStatus): Cta[] => {
  switch (signInStatus) {
    case SignInStatus.NOTSIGNEDIN:
      return [FREE_TRANSLATE_CTA, { label: PREMIUM_HOME_CTA, to: "/pricing" }];
    case SignInStatus.VERYFING:
      return [
        FREE_TRANSLATE_CTA,
        { label: "重新发送确认邮件", to: "/signup" },
      ];
    case SignInStatus.PURCHASING:
      return [
        FREE_TRANSLATE_CTA,
        { label: PREMIUM_PURCHASING_BUTTON, to: "/pricing" },
      ];
    case SignInStatus.PREMIUM:
      return [{ label: "点击此处在线翻译", to: "/translate" }];
    case SignInStatus.INITIALSTATE:
      return [];
  }
};

interface Props {
  signInStatus: SignInStatus;
}

const Home = (props: Props) => {
  // 認証状態の確定前は、確定後と同じ高さを確保して表示だけ隠す。
  // ボタンが後から現れてページがずれるのを防ぐ。
  const isResolving = props.signInStatus === SignInStatus.INITIALSTATE;
  const ctas = ctasFor(
    isResolving ? SignInStatus.NOTSIGNEDIN : props.signInStatus,
  );

  return (
    <Container component="main" sx={{ mb: 2 }} maxWidth="lg">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src="./imgs/logo.png"
          alt="Readable"
          style={{ width: "100%", maxWidth: 480 }}
        />
        <Typography
          variant="h5"
          component="h2"
          align="center"
          color="#84919e"
          sx={{ mt: 4, mb: 8 }}
        >
          将英文 PDF 翻译成中文，保留原有格式和版式。
        </Typography>
        <img
          src="./imgs/home_catch.png"
          alt="将英文 PDF 翻译成中文，保留原有格式和版式。"
          style={{ maxWidth: 1000, width: "100%" }}
        />
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          fontWeight="fontWeightMedium"
          align="center"
          sx={{ mt: 8, mb: 8 }}
        >
          在线翻译 PDF 文件
        </Typography>
        {/*
          MUI の Grid container は負のマージンで隙間を作るため、
          幅の狭い画面では左右の余白が非対称になる。CSS Grid の gap を使う。
        */}
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
            columnGap: 2,
            rowGap: 8,
          }}
        >
          {FEATURES.map((feature) => (
            <Box
              key={feature.src}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <img
                src={feature.src}
                alt={feature.labelLines.join("")}
                style={{ width: "100%", maxWidth: 350 }}
              />
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                align="center"
              >
                {feature.labelLines.map((line, index) => (
                  <Fragment key={line}>
                    {index > 0 && <br />}
                    {line}
                  </Fragment>
                ))}
              </Typography>
            </Box>
          ))}
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 8 }}
          sx={{
            mt: 8,
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            visibility: isResolving ? "hidden" : "visible",
          }}
        >
          {ctas.map((cta) => (
            <Button
              key={cta.label}
              variant="contained"
              to={cta.to}
              sx={{ width: { xs: "100%", sm: 250 }, maxWidth: 320, height: 40 }}
              component={RouterLink}
            >
              {cta.label}
            </Button>
          ))}
        </Stack>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          align="center"
          sx={{ mt: 16 }}
        >
          Pro 版功能介绍
        </Typography>
        <Box sx={{ width: "100%", maxWidth: 400, aspectRatio: "16 / 9" }}>
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/r_-tzhWvUOE?si=_Tq5BAFvOKL90WN_"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </Box>
      </Box>
    </Container>
  );
};

export default Home;
